import React from 'react';
import { observer } from 'mobx-react';
import CustomCircularProgress from '@gamiphy/loyalty-station-desktop/components/levels/components/CustomCircularProgress';
import { LevelStore } from '@gamiphy/loyalty-station-desktop/store/level';
import {
  Box, Button, LinearProgress, Stack, Typography,
} from '@mui/material';
import Benefits from '@gamiphy/loyalty-station-desktop/components/levels/Benefits';
import { Trans, useDisplayStringValue } from '@gamiphy/react-i18n';
import { usePointsLabel } from '@gamiphy/loyalty/points';
import { TierConfigType } from '@gamiphy/bl/level';
import { formatNumber, round } from '@gamiphy/utils/number';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { Tabs, UiStore } from '@gamiphy/loyalty-station-desktop/store/ui';
import { linearProgressClasses } from '@mui/material/LinearProgress';
import Tab from '../components/cashback/cashbackProgram/loggedIn/components/Tab';

const LevelsTab: React.FC = () => {
  const userTier = React.useMemo(
    () => LevelStore.currentTier ?? LevelStore.tiers[0],
    [LevelStore.tiers, LevelStore.currentTier],
  );
  const currentTierName = useDisplayStringValue(userTier?.name);
  const label = usePointsLabel(LevelStore.tierConfig?.type ?? TierConfigType.numberOfPurchases);
  const goToMissionsPage = React.useCallback(() => {
    UiStore.setTab(Tabs.Missions);
  }, []);

  const accumulationText = React.useMemo(() => {
    switch (LevelStore.tierConfig?.type) {
      case TierConfigType.numberOfPurchases:
        return (
          <Trans
            i18nKey="levels.accumulated.purchasesNumber"
            defaults="{{label}}: {{accumulated}}"
            values={{
              label,
              accumulated: formatNumber(round(LevelStore.accumulated)),
            }}
          />
        );
      case TierConfigType.valueOfPurchases:
        return (
          <Trans
            i18nKey="levels.accumulated.purchasesValue"
            defaults="{{label}}: {{accumulated, currency}}"
            values={{
              label,
              accumulated: LevelStore.accumulated,
            }}
          />
        );
      default:
        return (
          <Trans
            i18nKey="levels.accumulated.purchasesNumber"
            defaults="{{label}}: {{accumulated}}"
            values={{
              label,
              accumulated: formatNumber(round(LevelStore.accumulated)),
            }}
          />
        );
    }
  }, [LevelStore.tierConfig?.type, label, LevelStore.accumulated]);

  return (
    <Tab
      tab={Tabs.Levels}
      disablePadding
      header={(
        <Box pr={1.5}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box minWidth={35}>
              <Box component="img" src={userTier?.icon} maxHeight={40} />
            </Box>
            <Box>
              <Typography fontSize={16}>
                <Box component="span" fontWeight={700}>
                  <Trans i18nKey="realUser.tabs.levels" defaults="VIP Levels" />
                </Box>
                {' '}
                <Box component="span">
                  {`(${currentTierName})`}
                </Box>
              </Typography>
              <Typography fontSize={12}>
                {accumulationText}
              </Typography>
            </Box>
          </Box>
          <LinearProgress
            value={LevelStore.progress * 100}
            variant="determinate"
            sx={{
              mt: 1.5,
              width: 'calc(100% + 33px)',
              borderRadius: '100px',
              height: 8,
              bgcolor: '#EAEAEA',
              [`& .${linearProgressClasses.bar}`]: {
                bgcolor: userTier?.color,
              },
            }}
          />
        </Box>
        )}
    >
      <Stack
        textAlign="center"
        fontSize={14}
        spacing={2}
        alignItems="center"
      >
        <CustomCircularProgress progress={LevelStore.progress} />
        {!AppStore.isCashbackProgram && (
          <Box px={2} width="100%">
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={goToMissionsPage}
              sx={{
                backgroundColor: 'primary.main',
              }}
            >
              <Trans i18nKey="tier.points.earn" defaults="Earn {{, gems}}" values={{}} />
            </Button>
          </Box>
        )}
        <Box paddingInline={0.5} display="flex" justifyContent="center">
          <Benefits />
        </Box>
      </Stack>
    </Tab>
  );
};

export default observer(LevelsTab);
